import {
  clearChatState,
  getAvatarToken,
  setBotMessage,
  setCertificateSessionId,
  setMessages,
  setProductSessionId,
  setScenarioSessionId,
  setServerStatus,
} from "@/redux/slices/mlSlices/chatSlice";

import {useCallback, useEffect, useMemo, useRef} from "react";

import {
  AvatarSessionState,
  useAvatarLifecycleMachine,
} from "@hooks/HeygenAvatar/useAvatarLifecycleMachine";
import {useAvatarMedia} from "@hooks/HeygenAvatar/useAvatarMedia";
import {useAvatarTransport} from "@hooks/HeygenAvatar/useAvatarTransport";
import {
  LIVEAVATAR_MICROPHONE_PERMISSION_ERROR_CODE,
  LIVEAVATAR_START_PAYLOAD_ERROR_CODE,
  LIVEAVATAR_TOKEN_EMPTY_RESPONSE_ERROR_CODE,
  LIVEAVATAR_TOKEN_REQUEST_ERROR_CODE,
  LiveAvatarTokenError,
  normalizeLiveAvatarStartResponse,
} from "@hooks/HeygenAvatar/utils";
import {AvatarErrorPopup} from "@popups/simulationPopups/AvatarErrorPopup";
import {unwrapResult} from "@reduxjs/toolkit";
import {Room, RoomEvent} from "livekit-client";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import AvatarService from "@/services/AvatarService";
import {v4 as uuidv4} from "uuid";

const shouldLogRoomEvent = (eventType, topic) => {
  if (!eventType || topic !== "agent-response") return false;
  if (eventType === "avatar.transcription.chunk") return false;
  return true;
};

export const useHeygenAvatar = (
  openPopup = null,
  isSessionInitialized = false,
  hasAvatar = false,
  volume = 1,
  remainingTimeMs = null,
) => {
  const dispatch = useDispatch();
  const {t} = useTranslation();

  const {language} = useSelector((state) => state.common.settings);
  const {
    avatarId,
    voiceId,
    botMessage,
    product_session_id,
    messages,
    proxyList,
  } = useSelector((state) => state.ml.chat);

  const {
    avatarSessionStateRef,
    avatarSessionState,
    setAvatarSessionState,
    avatarState,
    setAvatarState,
    activityState,
    setActivityState,
    messageState,
    setMessageState,
    silenceState,
    setSilenceState,
  } = useAvatarLifecycleMachine();

  const logAvatarDebug = (...args) => {
    if (process.env.NODE_ENV === "production") return;
    const firstArg = String(args?.[0] || "").toLowerCase();
    const isAllowedLog =
      firstArg.includes("connect") ||
      firstArg.includes("reconnect") ||
      firstArg.includes("close") ||
      firstArg.includes("event");
    if (!isAllowedLog) return;
    console.log("[LiveAvatar]", ...args);
  };

  const startMessages = useMemo(() => [t("page.chat.start_message_1")], [t]);

  const liveAvatarLanguage = useMemo(() => {
    const normalized = String(language || "en").toLowerCase();
    const [primaryTag] = normalized.split("-");
    const supportedLanguages = new Set(["en", "ru", "de", "es", "fr"]);
    return supportedLanguages.has(primaryTag) ? primaryTag : "en";
  }, [language]);

  const liveAvatarPersonaConfig = useMemo(() => {
    const env = import.meta?.env || {};
    const contextId =
      env[`VITE_LIVEAVATAR_CONTEXT_ID_${liveAvatarLanguage.toUpperCase()}`] ||
      env.VITE_LIVEAVATAR_CONTEXT_ID ||
      null;

    const persona = {};
    if (voiceId) {
      persona.voice_id = voiceId;
    } else {
      persona.language = liveAvatarLanguage;
    }
    if (contextId) {
      persona.context_id = contextId;
    }
    return persona;
  }, [liveAvatarLanguage, voiceId]);

  const liveAvatarInteractivityType = useMemo(() => {
    const env = import.meta?.env || {};
    const value = String(env.VITE_LIVEAVATAR_INTERACTIVITY_TYPE || "").trim();
    return value ? value.toUpperCase() : null;
  }, []);

  const isLegacyIosSafari = useMemo(() => {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
      return false;
    }
    const ua = navigator.userAgent || "";
    const isIos = /iPad|iPhone|iPod/.test(ua);
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
    const versionMatch = ua.match(/OS (\d+)_/);
    const iosMajor = versionMatch ? Number(versionMatch[1]) : null;
    return isIos && isSafari && iosMajor !== null && iosMajor <= 15;
  }, []);

  const avatar = useRef(null);
  const roomRef = useRef(null);
  const mediaStream = useRef(null);
  const remoteStreamRef = useRef(new MediaStream());
  const remoteAudioStreamRef = useRef(new MediaStream());
  const remoteAudioElementRef = useRef(null);
  const microphonePermissionGrantedRef = useRef(false);
  const isStoppingRef = useRef(false);

  const {enableTransportBoundary, disableTransportBoundary} =
    useAvatarTransport({
      dispatch,
      proxyList,
    });

  useEffect(() => {
    if (typeof window === "undefined" || remoteAudioElementRef.current) return;
    const audio = new Audio();
    audio.autoplay = true;
    audio.playsInline = true;
    audio.preload = "auto";
    audio.volume = volume;
    remoteAudioElementRef.current = audio;

    return () => {
      if (remoteAudioElementRef.current) {
        remoteAudioElementRef.current.pause();
        remoteAudioElementRef.current.srcObject = null;
      }
      remoteAudioElementRef.current = null;
    };
  }, [volume]);

  useAvatarMedia({
    avatarSessionState,
    AvatarSessionState,
    avatarState,
    mediaStream,
    remoteAudioElementRef,
    volume,
    logAvatarDebug,
  });

  const fetchAccessToken = useCallback(
    async (sessionConfig) => {
      const resultAction = await dispatch(
        getAvatarToken({
          session: sessionConfig,
        }),
      );

      if (!getAvatarToken.fulfilled.match(resultAction)) {
        throw new Error("Unable to retrieve LiveAvatar session token");
      }

      const payload = unwrapResult(resultAction);
      if (!payload) {
        throw new LiveAvatarTokenError(
          LIVEAVATAR_TOKEN_EMPTY_RESPONSE_ERROR_CODE,
          "LiveAvatar token endpoint returned empty response",
        );
      }

      if (payload?.error) {
        throw new LiveAvatarTokenError(
          LIVEAVATAR_TOKEN_REQUEST_ERROR_CODE,
          "LiveAvatar token endpoint returned backend error",
          {backendError: payload.error},
        );
      }

      const data = payload?.data || payload;
      const sessionToken = data?.session_token || payload?.session_token;
      const sessionId = data?.session_id || payload?.session_id || null;
      const url = payload?.url || data?.url;

      if (!sessionToken) {
        throw new LiveAvatarTokenError(
          LIVEAVATAR_TOKEN_EMPTY_RESPONSE_ERROR_CODE,
          "Session token does not exist",
          {availableKeys: Object.keys(data || {})},
        );
      }

      if (!url) {
        throw new LiveAvatarTokenError(
          LIVEAVATAR_TOKEN_EMPTY_RESPONSE_ERROR_CODE,
          "LiveAvatar API URL does not exist",
          {availableKeys: Object.keys(payload || {})},
        );
      }

      return {
        sessionToken,
        sessionId,
        url: url.replace(/\/$/, ""),
      };
    },
    [dispatch],
  );

  const initAvatar = useCallback(({sessionToken, sessionId, url}) => {
    avatar.current = {
      sessionToken,
      sessionId,
      apiUrl: url,
    };
    return avatar.current;
  }, []);

  const publishCommandEvent = useCallback(async (eventType, eventData = {}) => {
    if (!roomRef.current || !avatar.current) {
      throw new Error("LiveAvatar room is not initialized");
    }

    const payload = {
      event_type: eventType,
      session_id: avatar.current.sessionId,
      ...eventData,
    };
    const encoded = new TextEncoder().encode(JSON.stringify(payload));

    await roomRef.current.localParticipant.publishData(encoded, {
      reliable: true,
      topic: "agent-control",
    });
  }, []);

  const startAvatar = useCallback(async () => {
    if (avatarSessionStateRef.current !== AvatarSessionState.INACTIVE) {
      throw new Error("There is already an active session");
    }
    if (!avatar.current?.sessionToken || !avatar.current?.apiUrl) {
      throw new Error("Avatar is not initialized");
    }

    avatarSessionStateRef.current = AvatarSessionState.CONNECTING;
    setAvatarSessionState(AvatarSessionState.CONNECTING);

    try {
      remoteStreamRef.current = new MediaStream();
      remoteAudioStreamRef.current = new MediaStream();

      const attachRemoteTrack = (track) => {
        const trackKind = String(track?.kind || "").toLowerCase();
        if (trackKind !== "video" && trackKind !== "audio") return;

        const mediaTrack = track?.mediaStreamTrack;
        if (!mediaTrack) return;

        const hasTrack = remoteStreamRef.current
          .getTracks()
          .some((existingTrack) => existingTrack.id === mediaTrack.id);

        if (!hasTrack) {
          remoteStreamRef.current.addTrack(mediaTrack);
          setAvatarState((prev) => ({
            ...prev,
            stream: remoteStreamRef.current,
          }));
        }

        if (trackKind === "audio") {
          const hasAudioTrack = remoteAudioStreamRef.current
            .getTracks()
            .some((existingTrack) => existingTrack.id === mediaTrack.id);

          if (!hasAudioTrack) {
            remoteAudioStreamRef.current.addTrack(mediaTrack);
          }

          if (remoteAudioElementRef.current) {
            remoteAudioElementRef.current.srcObject =
              remoteAudioStreamRef.current;
            remoteAudioElementRef.current.play().catch(() => {});
          }
        }
      };

      const startProxyResponse = await AvatarService.startAvatarSession(
        avatar.current.sessionToken,
      );

      const rawStartPayload = startProxyResponse.data;
      const normalizedStart = normalizeLiveAvatarStartResponse(rawStartPayload);
      const sessionDurationLimitSec =
        Number(avatar.current?.maxSessionDurationSec) || 0;
      const sessionDurationLimitMin =
        sessionDurationLimitSec > 0
          ? Number((sessionDurationLimitSec / 60).toFixed(2))
          : 0;
      const activityIdleTimeoutSec =
        Number(avatar.current?.activityIdleTimeoutSec) || 0;
      const activityIdleTimeoutMin = Number(
        (activityIdleTimeoutSec / 60).toFixed(2),
      );

      console.log(
        `[LiveAvatar] Session limits: session_duration = ${sessionDurationLimitSec}sec/${sessionDurationLimitMin}min, activity_iddle = ${activityIdleTimeoutSec}sec/${activityIdleTimeoutMin}min`,
      );

      if (normalizedStart.sessionId) {
        avatar.current.sessionId = normalizedStart.sessionId;
      }

      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
      });

      room.on(RoomEvent.TrackSubscribed, (track) => attachRemoteTrack(track));
      room.on(RoomEvent.TrackUnsubscribed, (track) => {
        const mediaTrack = track.mediaStreamTrack;
        if (!mediaTrack) return;
        remoteStreamRef.current.removeTrack(mediaTrack);
        remoteAudioStreamRef.current.removeTrack(mediaTrack);

        if (
          remoteAudioElementRef.current &&
          remoteAudioStreamRef.current.getTracks().length === 0
        ) {
          remoteAudioElementRef.current.pause();
          remoteAudioElementRef.current.srcObject = null;
        }

        setAvatarState((prev) => ({
          ...prev,
          stream:
            remoteStreamRef.current.getTracks().length > 0
              ? remoteStreamRef.current
              : null,
        }));
      });

      room.on(RoomEvent.DataReceived, (payload, _participant, _kind, topic) => {
        try {
          const decoded = new TextDecoder().decode(payload);
          const eventData = JSON.parse(decoded);
          const eventType = eventData?.event_type || eventData?.type || null;
          const incomingSessionId =
            eventData?.session_id || eventData?.sessionId || null;

          if (incomingSessionId && avatar.current) {
            avatar.current.sessionId = incomingSessionId;
          }

          if (shouldLogRoomEvent(eventType, topic)) {
            logAvatarDebug(`room data event: type "${eventType}"`);
          }

          if (topic !== "agent-response") return;

          switch (eventType) {
            case "avatar.speak_started":
              setActivityState((prev) => ({...prev, isAvatarTalking: true}));
              break;
            case "avatar.speak_ended":
              setActivityState((prev) => ({...prev, isAvatarTalking: false}));
              setSilenceState((prev) => ({...prev, stopVoice: true}));
              break;
            case "user.speak_started":
              setActivityState((prev) => ({...prev, isUserTalking: true}));
              break;
            case "user.speak_ended":
              setActivityState((prev) => ({...prev, isUserTalking: false}));
              break;
            case "session.stopped":
              if (isStoppingRef.current) {
                avatarSessionStateRef.current = AvatarSessionState.INACTIVE;
                setAvatarSessionState(AvatarSessionState.INACTIVE);
              }
              break;
            default:
              break;
          }
        } catch (error) {
          console.error("Error parsing LiveAvatar event:", error);
        }
      });

      room.on(RoomEvent.ConnectionQualityChanged, (quality) => {
        setActivityState((prev) => ({
          ...prev,
          connectionQuality: String(quality || "UNKNOWN"),
        }));
      });
      room.on(RoomEvent.Reconnecting, () => {
        logAvatarDebug("room reconnecting");
        avatarSessionStateRef.current = AvatarSessionState.CONNECTING;
        setAvatarSessionState(AvatarSessionState.CONNECTING);
      });
      room.on(RoomEvent.Reconnected, () => {
        logAvatarDebug("room reconnected");
        avatarSessionStateRef.current = AvatarSessionState.CONNECTED;
        setAvatarSessionState(AvatarSessionState.CONNECTED);
      });
      room.on(RoomEvent.Disconnected, () => {
        logAvatarDebug("room disconnected");
        remoteStreamRef.current = new MediaStream();
        remoteAudioStreamRef.current = new MediaStream();
        if (remoteAudioElementRef.current) {
          remoteAudioElementRef.current.pause();
          remoteAudioElementRef.current.srcObject = null;
        }
        setAvatarState((prev) => ({...prev, stream: null}));
        avatarSessionStateRef.current = AvatarSessionState.INACTIVE;
        setAvatarSessionState(AvatarSessionState.INACTIVE);
      });

      await room.connect(
        normalizedStart.livekitUrl,
        normalizedStart.livekitToken,
      );
      logAvatarDebug(
        `room connected: pID "${room.localParticipant?.sid || "unknown"}"`,
      );

      room.remoteParticipants.forEach((participant) => {
        participant.trackPublications?.forEach((publication) => {
          if (publication?.isSubscribed && publication?.track) {
            attachRemoteTrack(publication.track);
          }
        });
      });

      roomRef.current = room;
      avatarSessionStateRef.current = AvatarSessionState.CONNECTED;
      setAvatarSessionState(AvatarSessionState.CONNECTED);
      setAvatarState((prev) => ({
        ...prev,
        avatarData: normalizedStart.startData,
      }));
    } catch (error) {
      avatarSessionStateRef.current = AvatarSessionState.INACTIVE;
      setAvatarSessionState(AvatarSessionState.INACTIVE);
      throw error;
    }
  }, [
    avatarSessionStateRef,
    logAvatarDebug,
    setActivityState,
    setAvatarSessionState,
    setAvatarState,
    setSilenceState,
  ]);

  const stopAvatar = useCallback(async () => {
    if (isStoppingRef.current) return;
    isStoppingRef.current = true;

    try {
      if (roomRef.current) {
        roomRef.current.disconnect();
        roomRef.current = null;
      }

      if (avatar.current?.sessionToken) {
        await AvatarService.stopAvatarSession(
          avatar.current.sessionToken,
          avatar.current.sessionId || null,
        ).catch((error) => {
          console.error("Error stopping LiveAvatar session:", error);
        });
      }

      avatar.current = null;
      remoteStreamRef.current = new MediaStream();
      remoteAudioStreamRef.current = new MediaStream();
      if (remoteAudioElementRef.current) {
        remoteAudioElementRef.current.pause();
        remoteAudioElementRef.current.srcObject = null;
      }
      avatarSessionStateRef.current = AvatarSessionState.INACTIVE;
      setAvatarSessionState(AvatarSessionState.INACTIVE);
      setAvatarState({stream: null, avatarData: null});
      setActivityState({
        isAvatarTalking: false,
        isUserTalking: false,
        isListening: false,
        connectionQuality: "UNKNOWN",
      });
      setMessageState({
        lastSpokenMessage: "",
        firstMessageSent: false,
      });
      setSilenceState({
        silence: false,
        countdown: 115,
        stopVoice: true,
      });
    } catch (error) {
      console.error("Error stopping avatar:", error);
    } finally {
      isStoppingRef.current = false;
      disableTransportBoundary();
    }
  }, [
    avatarSessionStateRef,
    disableTransportBoundary,
    setActivityState,
    setAvatarSessionState,
    setAvatarState,
    setMessageState,
    setSilenceState,
  ]);

  const clearAllAvatarState = useCallback(async () => {
    try {
      await stopAvatar();
      dispatch(clearChatState());
      dispatch(setProductSessionId(null));
      dispatch(setScenarioSessionId(null));
      dispatch(setCertificateSessionId(null));
      dispatch(setServerStatus(""));
    } catch (error) {
      console.error("Error clearing avatar state:", error);
    }
  }, [dispatch, stopAvatar]);

  const ensureMicrophonePermission = useCallback(async () => {
    if (microphonePermissionGrantedRef.current) return;

    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices ||
      typeof navigator.mediaDevices.getUserMedia !== "function"
    ) {
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({audio: true});
      stream.getTracks().forEach((track) => track.stop());
      microphonePermissionGrantedRef.current = true;
    } catch (error) {
      console.warn("[LiveAvatar] Microphone permission denied, continuing without mic:", error?.message);
    }
  }, []);

  const speakText = useCallback(
    async (text, isSilenceMessage = false) => {
      if (
        !roomRef.current ||
        avatarSessionState !== AvatarSessionState.CONNECTED ||
        !String(text || "").trim()
      ) {
        return;
      }

      try {
        setSilenceState((prev) => ({
          ...prev,
          countdown: 115,
          silence: false,
          stopVoice: false,
        }));

        await publishCommandEvent("avatar.speak_text", {text});

        if (!isSilenceMessage) {
          setMessageState((prev) => ({...prev, lastSpokenMessage: text}));
        }
      } catch (error) {
        console.error("Error speaking:", error);
      }
    },
    [avatarSessionState, publishCommandEvent, setMessageState, setSilenceState],
  );

  const interruptAvatar = useCallback(async () => {
    if (!roomRef.current) return;
    try {
      await publishCommandEvent("avatar.interrupt");
    } catch (error) {
      console.error("Error interrupting avatar:", error);
    }
  }, [publishCommandEvent]);

  const firstProductMessage = useCallback(async () => {
    if (
      product_session_id &&
      messages.length === 0 &&
      !messageState.firstMessageSent
    ) {
      const text =
        startMessages[Math.floor(Math.random() * startMessages.length)];
      const startMessage = {messageId: uuidv4(), text, sender: "bot"};

      await dispatch(setBotMessage(text));
      await dispatch(setMessages(startMessage));
      setMessageState((prev) => ({...prev, firstMessageSent: true}));
    }
  }, [
    dispatch,
    messageState.firstMessageSent,
    messages.length,
    product_session_id,
    setMessageState,
    startMessages,
  ]);

  const initAvatarWithToken = useCallback(async () => {
    await ensureMicrophonePermission();
    await enableTransportBoundary();

    try {
      const maxHeygenSessionDurationSeconds = 1200;
      const idleTimeoutSecondsFromRemaining = Number.isFinite(remainingTimeMs)
        ? Math.floor((remainingTimeMs + 5 * 60 * 1000) / 1000)
        : maxHeygenSessionDurationSeconds;
      const activityIdleTimeout = Math.max(
        30,
        Math.min(maxHeygenSessionDurationSeconds, idleTimeoutSecondsFromRemaining),
      );

      const sessionConfig = {
        mode: "FULL",
        avatar_id: avatarId,
        quality: isLegacyIosSafari ? "medium" : "high",
        avatar_persona: liveAvatarPersonaConfig,
        activity_idle_timeout: activityIdleTimeout,
        max_session_duration: activityIdleTimeout,
        ...(liveAvatarInteractivityType
          ? {interactivity_type: liveAvatarInteractivityType}
          : {}),
      };

      const {sessionToken, sessionId, url} =
        await fetchAccessToken(sessionConfig);
      initAvatar({sessionToken, sessionId, url});
      if (avatar.current) {
        avatar.current.activityIdleTimeoutSec = activityIdleTimeout;
        avatar.current.maxSessionDurationSec = activityIdleTimeout;
      }
      await startAvatar();
    } catch (error) {
      disableTransportBoundary();
      throw error;
    }
  }, [
    avatarId,
    disableTransportBoundary,
    enableTransportBoundary,
    ensureMicrophonePermission,
    fetchAccessToken,
    initAvatar,
    isLegacyIosSafari,
    liveAvatarInteractivityType,
    liveAvatarPersonaConfig,
    remainingTimeMs,
    startAvatar,
  ]);

  const showAvatarError = useCallback(
    (onTryAgain, onExit, error = null) => {
      const detailedMessage =
        error?.code === LIVEAVATAR_START_PAYLOAD_ERROR_CODE
          ? t("popup.avatar_error.invalid_start_payload")
          : error?.code === LIVEAVATAR_TOKEN_EMPTY_RESPONSE_ERROR_CODE
            ? t("popup.avatar_error.token_empty_response")
            : error?.code === LIVEAVATAR_TOKEN_REQUEST_ERROR_CODE
              ? t("popup.avatar_error.token_request_error")
              : "";

      if (!openPopup) return;

      openPopup(
        <AvatarErrorPopup
          onClickTryAgain={onTryAgain}
          onClickExit={onExit}
          detailedMessage={detailedMessage}
        />,
        {closeButton: false},
      );
    },
    [openPopup, t],
  );

  useEffect(() => {
    if (
      botMessage &&
      avatarSessionState === AvatarSessionState.CONNECTED &&
      botMessage !== messageState.lastSpokenMessage
    ) {
      speakText(botMessage);
    }
  }, [
    avatarSessionState,
    botMessage,
    messageState.lastSpokenMessage,
    speakText,
  ]);

  useEffect(() => {
    if (
      product_session_id &&
      !messageState.firstMessageSent &&
      isSessionInitialized &&
      (!hasAvatar || avatarSessionState === AvatarSessionState.CONNECTED)
    ) {
      firstProductMessage();
    }
  }, [
    avatarSessionState,
    firstProductMessage,
    hasAvatar,
    isSessionInitialized,
    messageState.firstMessageSent,
    product_session_id,
  ]);

  useEffect(() => {
    if (silenceState.countdown <= 0) {
      setSilenceState((prev) => ({...prev, silence: true}));
      return;
    }

    if (avatarSessionState !== AvatarSessionState.CONNECTED) return;

    const timer = setInterval(() => {
      setSilenceState((prev) => ({...prev, countdown: prev.countdown - 1}));
    }, 1000);

    return () => clearInterval(timer);
  }, [avatarSessionState, setSilenceState, silenceState.countdown]);

  useEffect(() => {
    if (
      avatarSessionState === AvatarSessionState.CONNECTED &&
      silenceState.silence
    ) {
      speakText(t("page.chat.silence_message"), true);
    } else if (silenceState.silence) {
      setSilenceState((prev) => ({
        ...prev,
        countdown: 115,
        silence: false,
      }));
    }
  }, [avatarSessionState, setSilenceState, silenceState.silence, speakText, t]);

  useEffect(() => {
    return () => {
      clearAllAvatarState();
    };
  }, [clearAllAvatarState]);

  return {
    avatarSessionState,
    mediaStream,
    AvatarSessionState,
    stream: avatarState.stream,
    avatarData: avatarState.avatarData,
    isAvatarTalking: activityState.isAvatarTalking,
    isUserTalking: activityState.isUserTalking,
    isListening: activityState.isListening,
    connectionQuality: activityState.connectionQuality,
    silence: silenceState.silence,
    countdown: silenceState.countdown,
    stopVoice: silenceState.stopVoice,
    fetchAccessToken,
    initAvatar,
    stopAvatar,
    speakText,
    interruptAvatar,
    ensureMicrophonePermission,
    initAvatarWithToken,
    firstProductMessage,
    clearAllAvatarState,
    showAvatarError,
    setAvatarSessionState,
  };
};
